import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { isEmpty, keyBy } from 'lodash';

import EditableChip, { type EditableChipColors } from '~/components/core/Molecules/EditableChip';
import type { Category } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError } from '~/Utils';

interface EntityWithCategories {
  id: number;
  claim_id?: number;
  categories?: Array<Category>;
}

interface CategoryEditableChipBaseProps<TEntity extends EntityWithCategories> {
  entity: TEntity;
  categories: Category[];
  isLoading: boolean;
  isError: boolean;
  apiEndpoint: string;
  disabled?: boolean;
  setIsUpdating: (isUpdating: boolean) => void;
  onUpdate: (entityId: number, updates: Record<string, unknown>) => void | Promise<void>;
  className?: string;
  stopPropagation?: boolean;
}

const CategoryEditableChipBase = <TEntity extends EntityWithCategories>({
  entity,
  categories,
  isLoading,
  isError,
  apiEndpoint,
  disabled,
  setIsUpdating,
  onUpdate,
  className,
  stopPropagation = false,
}: CategoryEditableChipBaseProps<TEntity>) => {
  const entityCategory = entity.categories?.[0]?.key;
  const [categoryToDisplay, setCategoryToDisplay] = useState(entityCategory);
  const categoryByKeyMap = keyBy(categories, 'key');

  useEffect(() => {
    if (entityCategory) {
      setCategoryToDisplay(entityCategory);
    }
  }, [entityCategory]);

  const handleCategoryUpdate = async (category_key: string) => {
    const prevCategory = categoryToDisplay;
    try {
      setIsUpdating(true);
      setCategoryToDisplay(category_key);
      await axios.put(apiEndpoint, {
        claim_id: entity.claim_id,
        categories: [categoryByKeyMap[category_key].id],
      });
      await onUpdate(entity.id, { categories: [categoryByKeyMap[category_key]] });
    } catch (err) {
      await reportAxiosError(err);
      setCategoryToDisplay(prevCategory);
    } finally {
      setIsUpdating(false);
    }
  };

  return !isLoading && !isError && !isEmpty(categories) ? (
    <div onClick={stopPropagation ? (e: React.MouseEvent) => e.stopPropagation() : undefined}>
      <EditableChip
        value={categoryToDisplay}
        onChange={handleCategoryUpdate}
        options={categories.map(({ key, color, label, is_disabled }) => ({
          id: key,
          color: color as EditableChipColors,
          description: label,
          disabled: is_disabled,
        }))}
        disabled={disabled}
        className={className}
      />
    </div>
  ) : null;
};

export default CategoryEditableChipBase;
